"use client";

import { motion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { MCP_INSTALL_GUIDE_URL } from "@/app/(marketing)/_components/nav-links";
import { Container } from "@/app/(marketing)/_components/ui/container";

import { MCPHeroArtwork } from "./mcp-hero-artwork";

const SUPPORTED_PLATFORMS = [
  { name: "Claude Code", src: "/claude-color.svg" },
  { name: "OpenClaw", src: "/openclaw-color.svg" },
  { name: "Codex", src: "/openai.svg" },
  { name: "Lovable", src: "/lovable-color.svg" },
  { name: "Cursor", src: "/cursor.svg" },
  { name: "Claude Desktop", src: "/claude-color.svg" },
];

/**
 * MCP landing hero. Animated tool-stream artwork renders full-bleed behind the
 * copy. The extra bottom padding leaves room for the capability cards to
 * overlap up into the blue, mirroring the root landing page.
 */
export function MCPHero() {
  return (
    <section className="relative overflow-hidden pt-16 pb-40 md:pt-24 md:pb-52">
      <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
        <MCPHeroArtwork />
      </div>

      <Container>
        <motion.div
          initial={{ opacity: 0, transform: "translateY(20px)" }}
          animate={{ opacity: 1, transform: "translateY(0px)" }}
          transition={{ duration: 0.5 }}
          className="relative z-10 mx-auto max-w-[860px] text-center"
        >
          <div className="mb-6 flex justify-center">
            <div className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 backdrop-blur-sm">
              <p className="font-mono text-sm tracking-[-0.28px] text-white/80">
                The Expert Labor MCP
              </p>
            </div>
          </div>

          <h1 className="mb-6 text-4xl font-medium tracking-tighter text-balance text-white sm:text-[56px] sm:leading-[1.1] md:text-[64px] md:leading-[1.1]">
            Hire humans, train AI, and hear from customers, from inside your
            agent
          </h1>

          <p className="mx-auto mb-12 max-w-[700px] text-lg text-balance text-white/80">
            Connect your agent to verified human labor with one MCP install. It
            sources experts on demand, collects the human data your models need,
            and gathers feedback from real customers, then keeps moving.
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href={MCP_INSTALL_GUIDE_URL}
              className="text-marketing-blue group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium transition-colors hover:bg-white/90"
            >
              Install guide
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              href="#install"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
            >
              Install the MCP
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>

        <div className="relative z-10 mx-auto mt-20 flex w-full max-w-[1280px] flex-col items-center gap-8">
          <div className="flex w-full items-center justify-center gap-10">
            <div className="h-px flex-1 bg-white/20" />
            <span className="text-center text-sm leading-6 whitespace-nowrap text-white/60 uppercase">
              Works with your agent stack
            </span>
            <div className="h-px flex-1 bg-white/20" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-5 sm:gap-x-10 lg:gap-x-14">
            {SUPPORTED_PLATFORMS.map((platform) => (
              <div key={platform.name} className="flex items-center gap-2.5">
                <Image
                  src={platform.src}
                  alt={platform.name}
                  width={32}
                  height={32}
                  className="h-8 w-8 brightness-0 invert"
                  style={{ opacity: 0.6 }}
                />
                <span className="text-xl font-semibold whitespace-nowrap text-white/60">
                  {platform.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
